/**
 * Registration form (feature #2) — shown AFTER email-OTP sign-in.
 *
 * FREE-TIER MODEL: there are no Cloud Functions, so registration is a DIRECT
 * write of the caller's own `users/{uid}` doc with `status: 'pending'` (the
 * only create the rules allow). The EMAIL is the verified sign-in identity —
 * it comes from the auth token's `email` claim and is not editable here (the
 * rules also enforce doc.email == token.email). The form asks for exactly
 * three fields — company name, mobile number and city — all required; there is
 * no way to skip it.
 *
 * On submit → create users/{uid} pending → AuthContext's live listener flips
 * `phase` to 'signedIn'/status 'pending' → we route to the under-review screen.
 */
import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { CenteredScreen } from '../../components/Screen';
import { auth } from '../../lib/firebase';
import { toE164India } from '../../lib/format';
import { createOwnUserProfile, type NewUserProfile } from '../../lib/repo';
import { useAppConfig } from '../../hooks/useAppConfig';
import { ROUTES } from '../../routes/paths';

interface FormState {
  companyName: string;
  mobile: string;
  city: string;
}

const empty: FormState = {
  companyName: '',
  mobile: '',
  city: '',
};

export function RegisterScreen(): JSX.Element {
  const navigate = useNavigate();
  const config = useAppConfig();
  // Mirror of the Firestore rule: absent field = approval required.
  const approvalRequired = config?.approvalRequired ?? true;
  // Post-OTP: identity is the verified email from the auth token. Custom-token
  // sign-ins carry it as a claim (token.claims.email); user.email covers any
  // future email-provider sign-in.
  const [email, setEmail] = useState('');
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    if (user.email) {
      setEmail(user.email);
      return;
    }
    void user.getIdTokenResult().then((r) => {
      const claim = r.claims.email;
      if (typeof claim === 'string') setEmail(claim);
    });
  }, []);

  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = useCallback((): { ok: true; data: NewUserProfile } | { ok: false; msg: string } => {
    if (!form.companyName.trim()) return { ok: false, msg: 'Company name is required.' };
    const mobile = toE164India(form.mobile);
    if (!mobile) return { ok: false, msg: 'Enter a valid mobile number.' };
    if (!form.city.trim()) return { ok: false, msg: 'City is required.' };
    if (!email) return { ok: false, msg: 'Could not read your sign-in email. Please log in again.' };
    return {
      ok: true,
      data: {
        name: '', // not collected at signup; the dealer can add it from Profile
        mobile,
        email,
        companyName: form.companyName.trim(),
        city: form.city.trim(),
      },
    };
  }, [form, email]);

  const onSubmit = useCallback(async () => {
    setError(null);
    const uid = auth.currentUser?.uid;
    if (!uid) {
      // Should not happen (guarded route), but fail safe back to login.
      navigate(ROUTES.login, { replace: true });
      return;
    }
    const v = validate();
    if (!v.ok) {
      setError(v.msg);
      return;
    }
    setBusy(true);
    try {
      // Approval toggle OFF → the profile is created directly approved and the
      // dealer goes straight in; ON → classic pending + under-review wait.
      await createOwnUserProfile(uid, v.data, approvalRequired ? 'pending' : 'approved');
      navigate(approvalRequired ? ROUTES.underReview : ROUTES.splash, { replace: true });
    } catch {
      setError('Could not submit your registration. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [validate, navigate, approvalRequired]);

  return (
    <CenteredScreen title="Register" subtitle="Complete your dealer profile for ANYWAY IT SOLUTIONS.">
      <div className="space-y-3">
        <div>
          <label className="label">Email (your sign-in)</label>
          <input className="field bg-slate-100 text-slate-500" type="email" value={email} readOnly />
        </div>
        <Field label="Company name" value={form.companyName} onChange={set('companyName')} autoComplete="organization" />
        <Field
          label="Mobile number"
          value={form.mobile}
          onChange={set('mobile')}
          type="tel"
          placeholder="+91 98765 43210"
          autoComplete="tel"
        />
        <Field label="City" value={form.city} onChange={set('city')} autoComplete="address-level2" />
      </div>
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      <button
        type="button"
        className="btn-primary mt-5 w-full"
        disabled={busy}
        onClick={() => void onSubmit()}
      >
        {busy ? 'Submitting…' : approvalRequired ? 'Submit for review' : 'Save & continue'}
      </button>
      <button
        type="button"
        className="mt-3 w-full text-sm text-slate-500 underline"
        onClick={() => {
          void signOut(auth).then(() => navigate(ROUTES.login, { replace: true }));
        }}
      >
        Sign in with a different email
      </button>
    </CenteredScreen>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
}): JSX.Element {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        className="field"
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
    </div>
  );
}
